// @flow
import * as React from 'react';
import 'app/common/react/components/profile/showcase/ShowcaseContainer/ShowcaseContainer.scss';
import cx from 'classnames';
import NavigateLink from 'app/common/react/ui/NavigateLink';
import PropTypes from 'prop-types';
import ShowcaseCarousel from 'app/common/react/components/profile/showcase/ShowcaseCarousel/ShowcaseCarousel';
import ShowcaseInfo from 'app/common/react/components/profile/showcase/ShowcaseInfo/ShowcaseInfo';
import ShowcasePips from 'app/common/react/components/profile/showcase/ShowcasePips/ShowcasePips';
import storage from 'app/common/lib/Storage';
import { ANIMATION_DIRECTION } from 'app/common/react/components/profile/showcase/commonTypes';
import type {
  AnimationDirection,
  ShowcaseBoardType,
  ShowcaseArticleType,
  ShowcaseShopType,
} from 'app/common/react/components/profile/showcase/commonTypes';
import withResource, { type Resource } from 'app/common/lib/react/withResource';
import { Box, Container } from 'gestalt';

const AUTOPLAY_DURATION = 4000;
const DISMISS_EMPTY_SHOWCASE_STORAGE_KEY = 'hideEmptyShowcase';

type ShowcaseContainerPropType = {
  showShowcase: boolean,
  showcaseResource: Resource<Array<?(ShowcaseArticleType | ShowcaseBoardType | ShowcaseShopType)>>,
  username: string,
  viewingOwnProfile: boolean,
};

type ShowcaseContainerStateType = {
  animationDirection?: AnimationDirection,
  currentIndex: number,
  hideEmptySection: boolean,
  showcaseIntervalId?: number,
};

export class ShowcaseContainer extends React.Component<
  ShowcaseContainerPropType,
  ShowcaseContainerStateType,
> {
  static defaultProps = {
    sections: [],
  };

  state: ShowcaseContainerStateType = {
    currentIndex: 0,
    hideEmptySection: false,
  };

  componentDidMount() {
    this.getHideEmptySectionState();
  }

  UNSAFE_componentWillReceiveProps(nextProps: ShowcaseContainerPropType) {
    if (nextProps.showcaseResource.data && this.props.showcaseResource.isFetching) {
      this.handleAutoplay();
    }
  }

  componentWillUnmount() {
    if (this.state.showcaseIntervalId) {
      window.clearInterval(this.state.showcaseIntervalId);
    }
  }

  getHideEmptySectionState = () => {
    // Check if the Empty State has been dismissed
    const hideEmptySection = storage.localStorage.getItem(DISMISS_EMPTY_SHOWCASE_STORAGE_KEY);
    this.setState({ hideEmptySection: !!hideEmptySection });
  };

  goToNextSection = () => {
    const {
      showcaseResource: { data: sections },
    } = this.props;
    if (!sections) {
      return;
    }
    const currentIndex = this.state.currentIndex;
    const numSections = sections.length;
    if (numSections > 1) {
      this.setState({
        animationDirection: ANIMATION_DIRECTION.RIGHT,
        currentIndex: (currentIndex + 1) % numSections,
      });
    }
  };

  handleEmptyStateDismissal = () => {
    storage.localStorage.setItem(DISMISS_EMPTY_SHOWCASE_STORAGE_KEY, true);
    this.setState({ hideEmptySection: true });
  };

  handleShowcaseNextClick: (event: Object) => void = (event) => {
    event.stopPropagation();
    this.handleAutoplay();
    this.goToNextSection();
  };

  handleShowcaseBackClick: (event: Object) => void = (event) => {
    const {
      showcaseResource: { data: sections },
    } = this.props;

    if (!sections) {
      return;
    }

    event.stopPropagation();
    this.handleAutoplay();
    const currentIndex = this.state.currentIndex;
    const newIndex = currentIndex > 0 ? currentIndex - 1 : sections.length - 1;
    this.setState({ animationDirection: ANIMATION_DIRECTION.LEFT, currentIndex: newIndex });
  };

  handleAutoplay() {
    if (this.state.showcaseIntervalId) {
      window.clearInterval(this.state.showcaseIntervalId);
    }
    const intervalId = window.setInterval(this.goToNextSection, AUTOPLAY_DURATION);
    this.setState({ showcaseIntervalId: intervalId });
  }

  renderBackNextButtons = (): React.Element<any> => (
    <div>
      <button
        className={cx('Button', 'hasIcon', 'circle', 'showcaseButton', 'nextButton', 'bg-white')}
        onClick={this.handleShowcaseNextClick}
      >
        <em />
      </button>
      <button
        className={cx('Button', 'hasIcon', 'circle', 'showcaseButton', 'backButton', 'bg-white')}
        onClick={this.handleShowcaseBackClick}
      >
        <em />
      </button>
    </div>
  );

  renderEditButton = (): React.Element<any> => (
    <NavigateLink to={'/showcase/settings/'}>
      <button
        className={cx('Button', 'hasIcon', 'circle', 'showcaseButton', 'editButton', 'bg-white')}
      >
        <em />
      </button>
    </NavigateLink>
  );

  render() {
    if (!this.props.showcaseResource.data) {
      return null;
    }

    const {
      showcaseResource: { data: sections },
      viewingOwnProfile,
    } = this.props;
    if (sections.length === 0) {
      return null;
    }

    const { animationDirection, currentIndex, hideEmptySection } = this.state;

    const numberSections = sections.length;
    const currentSection = numberSections > 0 ? sections[currentIndex] : null;
    // only hide if the showcase has not been set.
    if (hideEmptySection && numberSections === 0) {
      return null;
    }

    return (
      <Container>
        <div className="ShowcaseContainer mb2 py3">
          <div className="showcaseCarouselWrapper">
            <ShowcaseCarousel
              animationDirection={animationDirection}
              currentIndex={currentIndex}
              onEmptyStateDismissal={this.handleEmptyStateDismissal}
              sections={sections}
              viewingOwnProfile={viewingOwnProfile}
            />
            {numberSections > 1 ? this.renderBackNextButtons() : null}
            <Box display="flex" bottom column={12} paddingX={1} paddingY={4}>
              <Box flex="grow" justifyContent="start">
                <ShowcaseInfo
                  animationDirection={animationDirection}
                  currentSection={currentSection}
                  viewingOwnProfile={viewingOwnProfile}
                />
              </Box>
              <Box flex="none" justifyContent="end" marginRight={4}>
                {numberSections > 1 && (
                  <Box justifyContent="end">
                    <ShowcasePips currentPipIndex={currentIndex} numberPips={numberSections} />
                  </Box>
                )}
                <Box justifyContent="end">
                  <div className="pull-right mt1">
                    {viewingOwnProfile && this.renderEditButton()}
                  </div>
                </Box>
              </Box>
            </Box>
          </div>
        </div>
      </Container>
    );
  }
}

ShowcaseContainer.propTypes = {
  username: PropTypes.string.isRequired,
  viewingOwnProfile: PropTypes.bool.isRequired,
};

export default withResource(
  {
    name: 'ShowcaseResource',
    key: 'showcaseResource',
    options: (props) => ({
      username: props.username,
    }),
  },
  { allowStale: false },
)(ShowcaseContainer);
